import React, { useEffect, useState, ChangeEvent } from 'react';
import axios from 'axios';
import { addLogo, deleteLogo, getAllLogo } from '../../services/Logo_service';
import { FaTrash, FaCheckCircle, FaRegTimesCircle } from 'react-icons/fa';
import { getUserDetails } from '../../services/User_service';
import { isValidRcs } from '../../utils/functions/validateRCSNumber';
import { compressImage, standardizeImageDimensions } from '../../utils/functions/imageCompressor';
import UploadLogoLoader from '../../components/layout/UploadLogoLoader';
import useAppContext from '../../hooks/useAppContext';
import { AppContextType } from '../../context/AppContext';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../../css/addLogoUser.css';
import { PaginationControls } from "../../components/PaginationControls";
import { ToastContainer } from 'react-toastify';
import { Alert } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

const API_URL = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const FILE_BASE = (
  (process.env.REACT_APP_FILES_BASE || '')
  || API_URL.replace(/\/api_alo$/, '')
).replace(/\/$/, '');

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const formatLogoName = (name: string) => {
  return name
    .normalize('NFD')                     
    .replace(/[\u0300-\u036f]/g, '')     
    .replace(/\s+/g, '_')                
    .replace(/[^a-zA-Z0-9_]/g, '_')     
    .replace(/_+/g, '_')                 
    .replace(/^_|_$/g, '')               
    .toUpperCase();
};
// -----------------------

const GestionLogo: React.FC = () => {
  const { logos, setLogos } = useAppContext() as AppContextType;

  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [canAddLogo, setCanAddLogo] = useState<boolean>(false);
  const userStatus = localStorage.getItem("userStatus") || "anonymous";

  const [currentPage, setCurrentPage] = useState<number>(1);
  const logosPerPage = 5;

  const checkImageDimensions = (file: File): Promise<{width: number, height: number}> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error("Impossible de charger l'image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const processLogoUpload = async (file: File) => {
    setLoading(true);
    setUploadError(null);
    try {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`Le fichier est trop volumineux. La taille maximale est de 5MB. Votre fichier fait ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        setLoading(false);
        return;
      }

      const imageBase64 = await convertFileToBase64(file);

      // --------- formatage nom du fichier ici ----------
      const formattedFileName = formatLogoName(file.name);
      const formattedLogoNom = formatLogoName(file.name); // optionnel pour SOAP
      // -------------------------------------------------

      const sSHA1 = "";
      const codeClient = "";
      const floId = "0";
      const logoLibele = "test";
      const logoInfo = "test";

      await addLogo(
        imageBase64,
        formattedFileName,
        sSHA1,
        codeClient,
        floId,
        logoLibele,
        logoInfo,
        formattedLogoNom
      );

      await fetchLogos();
    } catch (error) {
      console.error("Erreur lors de l'ajout du logo:", error);
      const err = error as any;
      if (err.response && err.response.data && err.response.data.message) {
        setUploadError(err.response.data.message);
      } else {
        setUploadError("Une erreur est survenue lors du téléchargement du logo.");
      }
    } finally {
      setLoading(false);
    }
  };

  // redimensionne seulement si > 360x360
  const processImageFile = async (file: File): Promise<File> => {
    try {
      const dimensions = await checkImageDimensions(file);
      if (dimensions.width > 360 || dimensions.height > 360) {
        const result = await Swal.fire({
          title: 'Image trop grande',
          html: `
            <p>Votre logo doit faire maximum <strong>360x360 pixels</strong>.</p>
            <p>Dimensions actuelles: <strong>${dimensions.width}x${dimensions.height} pixels</p>
            <p>Voulez-vous redimensionner automatiquement votre logo?</p>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Oui, redimensionner',
          cancelButtonText: 'Annuler'
        });

        if (!result.isConfirmed) {
          throw new Error('Redimensionnement annulé par l\'utilisateur');
        }
        return await standardizeImageDimensions(file);
      }

      if (file.size > MAX_FILE_SIZE) {
        const result = await Swal.fire({
          title: 'Fichier trop volumineux',
          html: `
            <p>Votre fichier fait <strong>${(file.size / (1024 * 1024)).toFixed(2)}MB</strong>.</p>
            <p>La taille maximale autorisée est de <strong>5MB</strong>.</p>
            <p>Voulez-vous compresser votre logo?</p>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Oui, compresser',
          cancelButtonText: 'Annuler'
        });

        if (!result.isConfirmed) {
          throw new Error('Compression annulée par l\'utilisateur');
        }
        return await compressImage(file, 4.9);
      }
      return file;
    } catch (error) {
      throw error;
    }
  };

const fetchLogos = async () => {
  try {
    const fetchedLogos = await getAllLogo();
    // console.log("🖼️ Logos récupérés depuis l'API :", fetchedLogos.map((l:any) => `${FILE_BASE}/${l.libelle}`));
    setLogos(fetchedLogos); 
    localStorage.setItem('logos', JSON.stringify(fetchedLogos));
  } catch (error) {
    console.error('Erreur lors de la récupération des logos:', error);
  }
};


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userDetails = await getUserDetails();
        setUser(userDetails);
        if (userDetails) {
          if (userDetails.user_type === 'entreprise' || userDetails.user_type === 'professionnel') {
            setCanAddLogo(true);
          } else if (userDetails.user_type === 'personne' && userDetails.code_SIREN && isValidRcs(userDetails.code_SIREN)) {
            setCanAddLogo(true);
          } else {
            setCanAddLogo(false);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'utilisateur :", error);
      }
    };

    fetchUser();
    fetchLogos();

    const handleLogosUpdated = (event: any) => {
      const { deletedLogoIds } = event.detail || {};
      if (deletedLogoIds && Array.isArray(deletedLogoIds)) {
        setLogos(prev => {
          const next = prev.filter(logo => !deletedLogoIds.includes(logo.id));
          localStorage.setItem('logos', JSON.stringify(next));
          return next;
        });
      } else {
        fetchLogos();
      }
    };

    window.addEventListener('logosUpdated', handleLogosUpdated as EventListener);
    window.addEventListener('userUpdated', handleLogosUpdated as EventListener);

    return () => {
      window.removeEventListener('logosUpdated', handleLogosUpdated as EventListener);
      window.removeEventListener('userUpdated', handleLogosUpdated as EventListener);
    };
  }, []);

  const openFullscreen = (image: string) => setFullscreenImage(image);
  const closeFullscreen = () => setFullscreenImage(null);

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!canAddLogo) return;
    setUploadError(null);
    try {
      const files = event.target.files;
      if (files && files[0]) {
        const file = files[0];
        setLoading(true);
        const processedFile = await processImageFile(file);
        setLoading(false);
        await processLogoUpload(processedFile);
      }
    } catch (error: any) {
      setLoading(false);
      if (error.message !== 'Redimensionnement annulé par l\'utilisateur' &&
          error.message !== 'Compression annulée par l\'utilisateur') {
        setUploadError(error.message || "Une erreur est survenue lors du traitement du fichier.");
      }
    }
    event.target.value = '';
  };

  const handleImageDelete = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Êtes-vous sûr ?',
        text: "Cette action est irréversible !",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler'
      });

      if (result.isConfirmed) {
        await deleteLogo(id);

        const updatedLogos = logos.filter(logo => logo.id !== id);
        setLogos(updatedLogos);
        localStorage.setItem('logos', JSON.stringify(updatedLogos));

        window.dispatchEvent(new CustomEvent("logosUpdated", {
          detail: { deletedLogoIds: [id] }
        }));

        Swal.fire('Supprimé !', 'Votre logo a été supprimé.', 'success');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du logo:', error);
      Swal.fire('Erreur !', 'Une erreur est survenue lors de la suppression.', 'error');
    }
  };

  const handleDropUpload = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!canAddLogo) return;
    event.preventDefault();
    setDragOver(false);
    setUploadError(null);

    const files = event.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      try {
        setLoading(true);
        const processedFile = await processImageFile(file);
        setLoading(false);
        await processLogoUpload(processedFile);
      } catch (error: any) {
        setLoading(false);
        if (error.message !== 'Redimensionnement annulé par l\'utilisateur' &&
            error.message !== 'Compression annulée par l\'utilisateur') {
          setUploadError(error.message || "Une erreur est survenue lors du traitement du fichier.");
        }
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleUpdateLogoStatus = async (logo_id: string | number, user_email: string, logo_status: string) => {
    try {
      const id = Number(logo_id);
      await axios.put(`${API_URL}/logo/update-status`, { logo_id: id, logo_status });
      await fetchLogos();

      try {
        await axios.post(`${API_URL}/email/notify-client`, {
          email: user_email,
          logo_status: logo_status === "accepted" ? "accepté" : "rejeté"
        });
      } catch (errMail) {
        console.warn('Notification email échouée (non bloquant) :', errMail);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      Swal.fire('Erreur', 'Impossible de mettre à jour le statut du logo', 'error');
    }
  };

  const filteredLogos =
    user?.role?.toString().toLowerCase() === 'admin'
      ? logos.filter(logo => logo.logo_status === 'pending')
      : logos.filter(logo => logo.user_id === user?.id);

  const totalPages = Math.ceil(filteredLogos.length / logosPerPage);
  const startIndex = (currentPage - 1) * logosPerPage;
  const endIndex = startIndex + logosPerPage;
  const currentLogos = filteredLogos.slice(startIndex, endIndex);

  if (userStatus === "anonymous") {
          return (
              <>
                  <ToastContainer />
                  <div className="gp-root container-fluid">
                      <div className="mt-5 text-center">
                          <Alert
                              className="custom-alert-white"
                              message="Module de gestion des logos indisponible"
                              description="Ravi de vous compter parmi nous ! Publiez votre première annonce et finalisez votre inscription pour profiter de ce module."
                              type="warning"
                              showIcon
                              icon={<ExclamationCircleFilled style={{ color: '#eb8635' }} />}
                              style={{
                                  fontSize: '1.1rem',
                              }}
                          />
                      </div>
                  </div>
              </>
          );
      }

  return (
    <div className="gestion-container">
      <UploadLogoLoader isOpen={loading} />
      {uploadError && (
        <div className="alert alert-danger mt-2">{uploadError}</div>
      )}

      <div className="image-count-enhanced fine-long">
        <div className="logo-count-label fine-long-label">Nombre de logos</div>
        <div className="logo-count-badge fine-long-badge">
          <span className="logo-count-number">{filteredLogos.length}</span>
        </div>
      </div>

      <div className="gestion">
        <div className="supprimer">
          <div className="logos-grid mb-3">
          {currentLogos
            .filter((logo) => logo !== null)
            .map((logo) => {
              const logoSrc = `${FILE_BASE}/${logo.libelle}`;
              return (
                <div key={logo.id} className="logo-grid-item">
                  <div className="card-logo-container">
                    <div className="logo-img-wrapper">
                      <img
                        src={logoSrc}
                        alt={`Logo ${logo.id}`}
                        className="card-img-top logo-img-responsive"
                        onClick={() => openFullscreen(logo.libelle)}
                      />
                    </div>


                    {fullscreenImage === logo.libelle && (
                      <div className="fullscreen-overlay" onClick={closeFullscreen}>
                        <div className="fullscreen-content">
                          <img src={`${FILE_BASE}/${logo.libelle}`} alt={`Logo ${logo.id}`} />
                        </div>
                      </div>
                    )}

                    {user?.role?.toString().toLowerCase() === 'admin' && (
                      <div className="logo-user-info-container"
                        style={{ display: logo.User ? 'block' : 'none', marginTop: '-10px', padding: '8px' }}>
                        <div className="logo-user-info-item">
                          <div className="logo-user-info-value">
                            {logo.User?.code_SIREN && (
                              <div><strong>Code SIREN :</strong> {logo.User?.code_SIREN}</div>
                            )}
                            {logo.User?.user_type && (
                              <div><strong>Client :</strong> {logo.User?.user_type === 'personne' ? 'Particulier' : 'Entreprise'}</div>
                            )}
                            {logo.User?.user_type === 'entreprise' || logo.User?.user_type === 'professionnel' ? (
                              <div><strong>Raison sociale :</strong> {logo.User?.raison_social}</div>
                            ) : (logo.User?.user_type !== 'entreprise' && logo.User?.user_type !== 'professionnel') ? (
                              <div><strong>Nom et prénom :</strong>{' '}{`${logo.User?.name || ''} ${logo.User?.firstname || ''}`.trim()}</div>
                            ) : null}
                            {logo.User?.email && (<div><strong>Email :</strong> {logo.User?.email}</div>)}
                            {logo.User?.adresse && (<div><strong>Adresse :</strong> {logo.User?.adresse}</div>)}
                            {logo.User?.postal_code && (<div><strong>Code Postal :</strong> {logo.User?.postal_code}</div>)}
                            {logo.logo_status && (<div><strong>Status :</strong> {logo.logo_status === "accepted" ? "Accepté" : logo.logo_status ==="pending" ? "En Attente" : "Refusé"}</div>)}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="card-body text-center">
                      {user?.role?.toString().toLowerCase() === 'admin' ? (
                        <div className="d-flex gap-2 justify-content-center align-items-center mt-2">
                          <button
                            className="btn btn-outline-success btn-status-action same-size-action"
                            onClick={() => handleUpdateLogoStatus(logo.id, logo.User?.email, 'accepted')}
                            disabled={logo.logo_status !== 'pending'}
                          >
                            <FaCheckCircle style={{marginRight: 4}}/> Accepter
                          </button>
                          <button
                            className="btn btn-outline-warning btn-status-action same-size-action"
                            onClick={() => handleUpdateLogoStatus(logo.id, logo.User?.email, 'rejected')}
                            disabled={logo.logo_status !== 'pending'}
                          >
                            <FaRegTimesCircle style={{marginRight: 4}}/> Refuser
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex flex-column align-items-center mt-2 gap-2">
                          <span className="badge badge-status" style={{background:'#e0e3ea',color:'#444',fontWeight:500,padding:'10px 20px',fontSize:'1em',borderRadius:'16px'}}>
                            <strong>Status :</strong>  {logo.logo_status === "accepted" ? "Accepté" : logo.logo_status ==="pending" ? "En Attente" : "Refusé"}
                          </span>
                          <button
                            className="btn"
                            onClick={() => handleImageDelete(logo.id)}
                            style={{background:'#f81814b6',color:'#fff9f9ff',fontWeight:500,padding:'10px 20px',fontSize:'1em'}}
                          >
                            <FaTrash style={{marginRight: 4}}/> Supprimer
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )})}
          </div>
        </div>

        {totalPages > 1 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}

        {user?.role?.toString().toLowerCase() !== 'admin' && (
          <div className="ajouter">
            {canAddLogo ? (
              <div
                className={`image-gallery ${dragOver ? 'drag-over' : ''}`}
                onDrop={handleDropUpload}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="image-container">
                  <div id="image-slot" onClick={() => document.getElementById('image-upload')?.click()}>
                    Ajoutez un nouveau logo ou glissez‑le ici
                    <p className="upload-limit-info">Dimension requise: maximum 360x360 pixels.</p>
                    <input
                      type="file"
                      id="image-upload"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                      accept="image/jpeg,image/jpg,image/png,image/jfif"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-center" >
                <p style={{
                  background: 'transparent',
                  color: "#fff"
                }}>
                  Pour ajouter un logo, il faut compléter votre numéro SIREN et que ce soit validé par l'administrateur, voici le lien pour le remplir{' '}
                  <Link to="/home/gestion-compte" className="addLogoUser-link"  style={{color: '#fff', textDecoration: 'none', fontSize: '1rem'}}>
                    « Gestion de mon compte »
                  </Link>.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionLogo;
